import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Mail, Play, Pause, X, Repeat, MessageSquareReply, Loader2 } from "lucide-react";
import { toast } from "sonner";

const itDate = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");
const STATO_LABEL = { attivo: "ATTIVO", in_pausa: "IN PAUSA", terminato: "TERMINATO", completato: "COMPLETATO" };
const STATO_CLS = {
  attivo: "border-emerald-300 bg-emerald-100 text-emerald-800",
  in_pausa: "border-amber-300 bg-amber-100 text-amber-800",
  terminato: "border-slate-300 bg-slate-100 text-slate-700",
  completato: "border-sky-300 bg-sky-100 text-sky-800",
};

export default function ProspectFunnelCard({ prospect, onChanged }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState("");
  const [changing, setChanging] = useState(false);

  useEffect(() => {
    api.get("/prospect-funnel-config").then((r) => {
      setCfg(r.data);
      const first = (r.data.order || [])[0];
      if (first) setSel(first);
    }).catch(() => setCfg(false));
  }, []);

  const fp = prospect?.funnel_prospect || null;
  const active = fp && (fp.stato === "attivo" || fp.stato === "in_pausa");

  const call = async (fn) => {
    setBusy(true);
    try { await fn(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const attiva = (tipo) => call(async () => {
    await api.post(`/prospects/${prospect.id}/funnel/attiva`, { tipo });
    toast.success(`Funnel «${cfg.funnels[tipo]?.nome}» attivato`);
    setChanging(false);
  });
  const pausa = (motivo) => call(async () => { await api.post(`/prospects/${prospect.id}/funnel/pausa`, { motivo }); toast.success("Funnel in pausa"); });
  const riprendi = () => call(async () => { await api.post(`/prospects/${prospect.id}/funnel/riprendi`); toast.success("Funnel ripreso"); });
  const termina = () => { if (!window.confirm("Terminare il funnel su questo prospect?")) return; call(async () => { await api.post(`/prospects/${prospect.id}/funnel/termina`, { motivo: "terminato manualmente" }); toast.success("Funnel terminato"); }); };
  const cambia = (tipo) => call(async () => { await api.post(`/prospects/${prospect.id}/funnel/cambia`, { tipo }); toast.success(`Funnel cambiato: «${cfg.funnels[tipo]?.nome}»`); setChanging(false); });

  if (cfg === null) return <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="prospect-funnel-loading"><Loader2 className="h-4 w-4 animate-spin" /> Funnel…</div>;
  if (cfg === false) return null;

  const funnelName = active && fp ? (cfg.funnels[fp.tipo]?.nome || fp.tipo) : null;
  const totalSteps = active && fp ? (cfg.funnels[fp.tipo]?.steps?.length || 0) : 0;
  const stepNum = active && fp ? Math.min((fp.step_corrente ?? 0) + 1, totalSteps) : 0;

  return (
    <div data-testid="prospect-funnel-card">
      <div className="mb-3 flex items-center gap-2">
        <Mail className="h-4 w-4 text-primary" />
        <h2 className="font-heading text-lg font-bold tracking-tight">Funnel email</h2>
        {active && fp && <Badge className={`text-[10px] ${STATO_CLS[fp.stato]}`} data-testid="prospect-funnel-stato">{STATO_LABEL[fp.stato]}</Badge>}
      </div>

      {!cfg.attivo_globale && (
        <p className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800" data-testid="prospect-funnel-global-off">
          Il motore dei funnel prospect è <b>spento a livello globale</b>: le email non partiranno finché non viene attivato nelle impostazioni Funnel.
        </p>
      )}

      {!active && (
        <div className="space-y-3" data-testid="prospect-funnel-none">
          <p className="text-sm text-muted-foreground">
            {fp?.stato === "terminato" ? `Ultimo funnel terminato (${fp.terminato_motivo || "—"}).` : fp?.stato === "completato" ? "Sequenza completata." : "Nessun funnel attivo."}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <select className="h-9 rounded-md border border-border bg-background px-2 text-sm" value={sel} onChange={(e) => setSel(e.target.value)} data-testid="prospect-funnel-select">
              {(cfg.order || []).map((fid) => <option key={fid} value={fid}>{fid} · {cfg.funnels[fid]?.nome}</option>)}
            </select>
            <Button size="sm" disabled={busy || !sel} onClick={() => attiva(sel)} data-testid="prospect-funnel-attiva-btn">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />} Attiva funnel
            </Button>
          </div>
          {sel && cfg.funnels[sel]?.obiettivo && <p className="text-xs text-muted-foreground">{cfg.funnels[sel].obiettivo}</p>}
        </div>
      )}

      {active && fp && (
        <div className="space-y-3" data-testid="prospect-funnel-active">
          <div className="rounded-md border border-border bg-muted/30 p-3 text-sm">
            <div className="font-semibold text-foreground" data-testid="prospect-funnel-name">Funnel: {fp.tipo} · {funnelName}</div>
            <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <div>Attivato: <span className="font-mono text-foreground">{itDate(fp.attivato_il)}</span></div>
              <div>Prossima email: <span className="font-mono text-foreground">{fp.stato === "in_pausa" ? "in pausa" : itDate(fp.prossima_email_il)}</span></div>
              <div>Step: <span className="font-mono text-foreground">{stepNum} di {totalSteps}</span></div>
              {fp.stato === "in_pausa" && <div>Pausa: <span className="text-foreground">{fp.pausa_motivo || "—"}</span></div>}
            </div>
          </div>

          {!changing ? (
            <div className="flex flex-wrap gap-2">
              {fp.stato === "attivo"
                ? <Button size="sm" variant="outline" disabled={busy} onClick={() => pausa("manuale")} data-testid="prospect-funnel-pausa-btn"><Pause className="mr-1 h-4 w-4" /> Pausa</Button>
                : <Button size="sm" variant="outline" disabled={busy} onClick={riprendi} data-testid="prospect-funnel-riprendi-btn"><Play className="mr-1 h-4 w-4" /> Riprendi</Button>}
              <Button size="sm" variant="outline" disabled={busy} onClick={() => setChanging(true)} data-testid="prospect-funnel-cambia-btn"><Repeat className="mr-1 h-4 w-4" /> Cambia funnel</Button>
              {fp.stato === "attivo" && <Button size="sm" variant="outline" disabled={busy} onClick={() => pausa("risposta")} data-testid="prospect-funnel-risposta-btn"><MessageSquareReply className="mr-1 h-4 w-4" /> Ha risposto</Button>}
              <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" disabled={busy} onClick={termina} data-testid="prospect-funnel-termina-btn"><X className="mr-1 h-4 w-4" /> Termina</Button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <select className="h-9 rounded-md border border-border bg-background px-2 text-sm" value={sel} onChange={(e) => setSel(e.target.value)} data-testid="prospect-funnel-cambia-select">
                {(cfg.order || []).map((fid) => <option key={fid} value={fid}>{fid} · {cfg.funnels[fid]?.nome}</option>)}
              </select>
              <Button size="sm" disabled={busy || !sel} onClick={() => cambia(sel)} data-testid="prospect-funnel-cambia-conferma">Conferma cambio</Button>
              <Button size="sm" variant="ghost" onClick={() => setChanging(false)}>Annulla</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
